<template>
  <el-drawer
    :visible.sync="visible"
    size="1400px"
    :before-close="handleClose"
    @opened="openInit"
    custom-class="p-0"
  >
    <template #title>
      <div>
        <p>受众数据面板</p>
        <div class="text-xs text-[#999] pt-[8px]">{{ currentRow.group_name }}</div>
      </div>
    </template>
    <div class="bg-[#f6f4f4] p-4">
      <div class="flex w-full justify-end items-center mb-6">
        <div class="flex">
          <div class="mr-4">
            <el-button
              type="primary"
              size="small"
              icon="el-icon-edit"
              @click="handleEditAudience"
              >修改受众</el-button
            >
          </div>
          <adDateRangePick
            v-model="localDateRange"
            :hasTimeWindow="false"
            size="small"
            :disabled="!disabled"
          />
        </div>
      </div>
      <el-collapse
        v-model="activeNames"
        class="p-3"
        @change="handleCollapseChange"
      >
        <el-collapse-item
          title="受众群体效果"
          name="audience"
        >
          <div class="bg-white rounded-lg">
            <div
              v-if="loadingStates.audience"
              v-loading="true"
              class="flex justify-center items-center h-32"
            ></div>
            <AudienceOverviewChart
              v-else
              :data="audienceData.audience"
              :currency="currentRow.account_currency"
            />
          </div>
        </el-collapse-item>

        <el-collapse-item
          title="细分受众群"
          name="segment"
        >
          <div class="bg-white rounded-lg">
            <div
              v-if="loadingStates.segment"
              v-loading="true"
              class="flex justify-center items-center h-32"
            ></div>
            <AudienceSegmentChart
              v-else
              :data="audienceData.segment"
              :currency="currentRow.account_currency"
            />
          </div>
        </el-collapse-item>

        <el-collapse-item
          title="受众特征 (年龄、性别、家庭收入、育儿状况)"
          name="demographics"
        >
          <div class="bg-white rounded-lg">
            <div
              v-if="loadingStates.demographics"
              v-loading="true"
              class="flex justify-center items-center h-32"
            ></div>
            <AudienceFeature
              v-else
              :data="audienceData.demographics"
              :currency="currentRow.account_currency"
            />
          </div>
        </el-collapse-item>

        <el-collapse-item
          title="地理位置"
          name="locations"
        >
          <div class="bg-white rounded-lg">
            <div
              v-if="loadingStates.locations"
              v-loading="true"
              class="flex justify-center items-center h-32"
            ></div>
            <GeographicLocationChart
              v-else
              :data="audienceData.locations"
              :currency="currentRow.account_currency"
            />
          </div>
        </el-collapse-item>
      </el-collapse>
    </div>
  </el-drawer>
</template>

<script>
import AudienceSegmentChart from './components/AudienceSegmentChart.vue';
import AudienceFeature from './components/AudienceFeature.vue';
import AudienceOverviewChart from './components/AudienceOverviewChart.vue';
import GeographicLocationChart from './components/GeographicLocationChart.vue';
import { adDateRangePick } from '@adCommonComponent';
import { generateUUID } from './index';

import {
  getLocationsReport,
  getAudienceDemographicsReport,
  getAudienceSegmentReport,
  getAudienceReport,
} from '@/api/google/adManagement';

export default {
  name: 'AudienceDataModal',
  components: {
    AudienceSegmentChart,
    AudienceFeature,
    AudienceOverviewChart,
    GeographicLocationChart,
    adDateRangePick,
  },
  props: {
    // 控制弹窗显示
    visible: {
      type: Boolean,
      default: false,
    },
    currentRow: {
      type: Object,
      default: () => {
        return {
          account_currency: 'USD',
        };
      },
    },
    dateRange: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      localDateRange: {},
      activeNames: ['audience', 'segment', 'demographics', 'locations'],
      audienceData: {
        audience: null,
        segment: null,
        demographics: null,
        locations: null,
      },
      loadingStates: {
        audience: false,
        segment: false,
        demographics: false,
        locations: false,
      },
    };
  },
  watch: {
    dateRange: {
      handler(val) {
        this.localDateRange = val;
      },
      immediate: true,
    },
    localDateRange(val) {
      this.$emit('update:dateRange', val);
      if (this.visible && val && val.dateRange && val.dateRange.length === 2) {
        this.openInit();
      }
    },
  },
  computed: {
    disabled() {
      return !Object.values(this.loadingStates).some((state) => state);
    },
  },
  methods: {
    async fetchAudienceReport(params) {
      this.loadingStates.audience = true;
      try {
        const response = await getAudienceReport(params);
        if (response.code == 0) {
          this.audienceData.audience = response.data || {};
        } else {
          this.audienceData.audience = {};
        }
      } catch (error) {
        console.error('获取受众概览数据失败:', error);
        this.$message.error('获取受众概览数据失败');
      } finally {
        this.loadingStates.audience = false;
        this.$nextTick(() => {
          window.dispatchEvent(new Event('resize'));
        });
      }
    },
    async fetchSegmentReport(params) {
      this.loadingStates.segment = true;
      try {
        const response = await getAudienceSegmentReport(params);
        if (response.code == 0) {
          this.audienceData.segment = response.data || {};
        } else {
          this.audienceData.segment = {};
        }
      } catch (error) {
        console.error('获取受众细分数据失败:', error);
        this.$message.error('获取受众细分数据失败');
      } finally {
        this.loadingStates.segment = false;
        this.$nextTick(() => {
          window.dispatchEvent(new Event('resize'));
        });
      }
    },
    async fetchDemographicsReport(params) {
      this.loadingStates.demographics = true;
      try {
        const response = await getAudienceDemographicsReport(params);
        if (response.code == 0) {
          this.audienceData.demographics = response.data || {};
        } else {
          this.audienceData.demographics = {};
        }
      } catch (error) {
        console.error('获取人口统计数据失败:', error);
        this.$message.error('获取人口统计数据失败');
      } finally {
        this.loadingStates.demographics = false;
        this.$nextTick(() => {
          window.dispatchEvent(new Event('resize'));
        });
      }
    },
    async fetchLocationsReport(params) {
      this.loadingStates.locations = true;
      try {
        const response = await getLocationsReport(params);
        if (response.code == 0) {
          this.audienceData.locations = response.data || {};
          delete this.audienceData.locations['estimateDiffMetrics'];
        } else {
          this.audienceData.locations = {};
        }
      } catch (error) {
        console.error('获取地理位置数据失败:', error);
        this.$message.error('获取地理位置数据失败');
      } finally {
        this.loadingStates.locations = false;
      }
    },
    async openInit() {
      const params = {
        accountId: this.currentRow.account_id,
        adGroupId: this.currentRow.group_id,
        campaignId: this.currentRow.campaign_id,
        startDate: this.localDateRange.dateRange[0],
        endDate: this.localDateRange.dateRange[1],
        requestId: generateUUID(),
      };

      // 并行执行所有请求，互不影响
      this.fetchAudienceReport(params);
      this.fetchSegmentReport(params);
      this.fetchDemographicsReport(params);
      this.fetchLocationsReport(params);
    },
    handleClose() {
      // 通过 $emit 通知父组件关闭弹窗
      this.$emit('update:visible', false);
    },

    // 点击编辑受众
    handleEditAudience() {
      this.$emit('editAudience', this.currentRow);
    },
    // 刷新数据
    refreshData() {
      if (this.visible) {
        this.openInit();
      }
    },
    // 处理折叠面板变化
    handleCollapseChange() {
      this.$nextTick(() => {
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
        }, 10);
      });
    },
  },
};
</script>

<style scoped>
/* 使用 Tailwind CSS 替代传统CSS，通常只需要全局引入 Tailwind */
/* 如果需要覆盖 Element UI 的样式，可以在这里添加 */
:deep(.el-dialog) {
  border-radius: 8px !important;
}
/* 移除 el-dialog 默认的 padding，因为我们使用 div.p-6 包裹内容 */
:deep(.el-dialog__body) {
  padding: 0 !important;
}

/* 手风琴标题样式 */
:deep(.el-collapse-item__header) {
  font-size: 16px !important;
  font-weight: bold !important;
  padding: 0 20px !important;
  height: 60px;
  line-height: 60px;
}

/* 手风琴项目间隙 */
:deep(.el-collapse-item) {
  margin-bottom: 16px !important;
}

:deep(.el-collapse-item:last-child) {
  margin-bottom: 0 !important;
}
</style>
